/**
 * Utility functions for handling API errors and extracting server error messages
 */

/**
 * Extracts error message from various error sources
 * @param error - The error object (can be Error, Response, or any object)
 * @param fallbackMessage - Default message to use if no server message is found
 * @returns Promise<string> - The extracted error message
 */
export const extractErrorMessage = async (error: any, fallbackMessage: string = 'An error occurred'): Promise<string> => {
    try {
        // If error has a response property (fetch response)
        if (error?.response) {
            try {
                const responseText = await error.response.text();
                const responseJson = JSON.parse(responseText);
                if (responseJson.message) {
                    return responseJson.message;
                }
            } catch {
                // If parsing fails, try to use response text directly
                const responseText = await error.response.text();
                if (responseText && responseText.trim()) {
                    return responseText;
                }
            }
        }

        // If error is a Response object directly
        if (error instanceof Response) {
            try {
                const responseText = await error.text();
                const responseJson = JSON.parse(responseText);
                if (responseJson.message) {
                    return responseJson.message;
                }
            } catch {
                // If parsing fails, try to use response text directly
                const responseText = await error.text();
                if (responseText && responseText.trim()) {
                    return responseText;
                }
            }
        }

        // If error has message property
        if (error?.message && typeof error.message === 'string') {
            return error.message;
        }

        // If error has a data.message property (some API formats)
        if (error?.data?.message && typeof error.data.message === 'string') {
            return error.data.message;
        }

        // If error has error.message property (nested error formats)
        if (error?.error?.message && typeof error.error.message === 'string') {
            return error.error.message;
        }

        // If error is a string
        if (typeof error === 'string') {
            return error;
        }

        // If error has details property (some API formats)
        if (error?.details && typeof error.details === 'string') {
            return error.details;
        }

    } catch (e) {
        console.warn('Failed to extract error message:', e);
    }

    return fallbackMessage;
};

/**
 * Handles fetch response errors by extracting error messages from response body
 * @param response - The fetch Response object
 * @param fallbackMessage - Default message to use if no server message is found
 * @returns Promise<string> - The extracted error message
 */
export const handleResponseError = async (response: Response, fallbackMessage: string = 'Request failed'): Promise<string> => {
    try {
        const responseText = await response.text();
        
        // Try to parse as JSON first
        try {
            const responseJson = JSON.parse(responseText);
            if (responseJson.message) {
                return responseJson.message;
            }
            if (responseJson.error) {
                return responseJson.error;
            }
            if (responseJson.details) {
                return responseJson.details;
            }
        } catch {
            // If JSON parsing fails, use the text directly if it's meaningful
            if (responseText && responseText.trim() && responseText.length < 200) {
                return responseText.trim();
            }
        }
    } catch (e) {
        console.warn('Failed to extract error from response:', e);
    }

    return fallbackMessage;
};

/**
 * Creates a user-friendly error message by combining operation context with server message
 * @param operation - The operation that failed (e.g., "save match", "delete question")
 * @param error - The error object
 * @returns Promise<string> - User-friendly error message
 */
export const createUserFriendlyErrorMessage = async (operation: string, error: any): Promise<string> => {
    const serverMessage = await extractErrorMessage(error);
    
    // If we got a meaningful server message, use it with context
    if (serverMessage && serverMessage !== 'An error occurred') {
        return `Failed to ${operation}: ${serverMessage}`;
    }
    
    // Fall back to generic message
    return `Failed to ${operation}`;
};